import {
  Children,
  Fragment,
  isValidElement,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useMediaQuery } from "usehooks-ts";

import { BuiBreadcrumb } from "@btnet/blueprint-components/BuiBreadcrumb";
import { IconDotsThree } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import { BTButton } from "../BTButton/BTButton";
import { BTDropdown } from "../BTDropdown/BTDropdown";
import { BTMenu } from "../BTMenu/BTMenu";
import { useResizeObserver } from "../hooks/useResizeObserver/useResizeObserver";
import {
  type IBTBreadcrumbItemProps,
  type IBTBreadcrumbProps,
} from "./BTBreadcrumb.types";

/** Below this many items there is no middle to collapse, so overflow tracking is skipped. */
const MIN_COLLAPSIBLE_ITEMS = 3;

/**
 * Flattens children into breadcrumb items, descending through fragments.
 *
 * `Children.toArray` counts a `<>…</>` wrapper as ONE child, but every `breadcrumbSlot` consumer
 * passes exactly that shape — so trails silently fell under `MIN_COLLAPSIBLE_ITEMS` and wrapped
 * while antd (which recurses) still rendered every crumb.
 * @param children Breadcrumb children, possibly wrapped in one or more fragments.
 */
const flattenItems = (
  children: ReactNode,
): ReactElement<IBTBreadcrumbItemProps>[] =>
  Children.toArray(children)
    .filter(isValidElement<IBTBreadcrumbItemProps>)
    .flatMap((child) =>
      child.type === Fragment ? flattenItems(child.props.children) : [child],
    );

/**
 * Recursively collects the string/number content rendered inside a crumb. Booleans, null, and
 * undefined render nothing in React and contribute nothing here.
 * @param node A crumb's children, at any nesting depth.
 */
const extractTextContent = (node: ReactNode): string => {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractTextContent).join("");
  }
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return extractTextContent(node.props.children);
  }
  return "";
};

/**
 * Identifies WHICH trail the latched expand threshold belongs to, so it resets on a new folder
 * path or a rename and holds otherwise.
 *
 * Keyed on text rather than the obvious candidates for good reason. Child identity is fresh every
 * render (consumers build children inline), and a reset expands unconditionally — so an
 * identity key would expand-probe on every render and close an open dropdown. A testid tracks
 * neither width nor renames: it can be a static literal, or an id that survives the rename that
 * changed the width. Text is what actually drives the measurement being latched.
 * @param childArray The flattened breadcrumb items.
 */
const getTrailSignature = (
  childArray: ReactElement<IBTBreadcrumbItemProps>[],
): string =>
  JSON.stringify(
    childArray.map((child) => extractTextContent(child.props.children)),
  );

/**
 * Extra width the parent must regain, beyond the value latched at collapse time, before a
 * collapsed breadcrumb expands again.
 *
 * NOT the anti-oscillation mechanism — the ratchet in the layout effect is. This guards the one
 * cycle the ratchet cannot damp: with a classic (non-gutter) scrollbar, collapsing can shorten the
 * page, remove the scrollbar, and widen the parent by ~17px, which lets the expand probe succeed,
 * brings the scrollbar back, and loops. Only a margin wider than a scrollbar breaks it.
 *
 * Deliberately untested and currently unreachable — every consumer's scroll container either
 * reserves gutter space or cannot overflow. It exists for the next one that doesn't; don't delete
 * it for lacking coverage.
 */
const EXPAND_HYSTERESIS_PX = 32;

/**
 * Whether the breadcrumb row has wrapped onto more than one line.
 *
 * The row is `flex-wrap: wrap`, so it wraps instead of growing `scrollWidth` — the usual
 * `clientWidth < scrollWidth` overflow check reports nothing here.
 * @param navEl The `<nav>` wrapper; its first element child is the antd breadcrumb row.
 */
const isWrapped = (navEl: HTMLElement): boolean => {
  const row = navEl.firstElementChild;
  if (!row) {
    return false;
  }

  const [first, ...rest] = Array.from(row.children) as HTMLElement[];
  if (!first || rest.length === 0) {
    return false;
  }

  return rest.some((item) => item.offsetTop > first.offsetTop);
};

interface ICollapsedBreadcrumbItemProps {
  testid: string;
  separator: ReactNode;
  collapsedItems: ReactElement<IBTBreadcrumbItemProps>[];
}

/**
 * Collapsed breadcrumb item that shows middle items in a dropdown menu.
 * BTDropdown handles keyboard accessibility (focus management, Escape) internally.
 * triggerRef enables focus return to the button when the menu closes.
 */
const CollapsedBreadcrumbItem = ({
  testid,
  separator,
  collapsedItems,
}: ICollapsedBreadcrumbItemProps) => {
  const triggerRef = useRef<HTMLElement>(null);

  const menu = (
    <BTMenu testid={`${testid}-menu`}>
      {collapsedItems.map((child, index) => (
        <BTMenu.Item
          key={index}
          testid={`${testid}-item-${index}`}
          // Consumers split on where a crumb's navigation lives: most wrap the label in
          // a link inside `children` (survives collapse for free), but some put it on
          // the Item with a bare string child — those crumbs were inert text in the
          // dropdown until this forwarded it. Any future crumb prop needs the same
          // treatment; only `children` comes across on its own.
          //
          // The cast is deliberate. rc-menu routes Enter on a focused item through
          // `onClick` too, so `info.domEvent` may be a KeyboardEvent — narrowing on
          // `domEvent.type === "click"` would make collapsed crumbs dead to keyboard
          // users. Every crumb handler in the tree ignores its event argument.
          {...(child.props.onClick !== undefined && {
            onClick: (info: { domEvent: unknown }) =>
              child.props.onClick?.(
                info.domEvent as MouseEvent<
                  HTMLAnchorElement | HTMLSpanElement
                >,
              ),
          })}
        >
          {child.props.children}
        </BTMenu.Item>
      ))}
    </BTMenu>
  );

  return (
    <BuiBreadcrumb.Item testid={testid} separator={separator}>
      <BTDropdown
        overlay={menu}
        trigger={["click"]}
        triggerRef={triggerRef}
        testid={`${testid}-dropdown`}
      >
        <BTButton
          ref={triggerRef}
          type="tertiary"
          data-testid={`${testid}-trigger`}
          icon={<IconDotsThree />}
          aria-label="Show collapsed breadcrumbs"
        />
      </BTDropdown>
    </BuiBreadcrumb.Item>
  );
};

/**
 * Hook that manages BTBreadcrumb state and responsive collapse behavior.
 * Returns props for the nav wrapper, BuiBreadcrumb, and processed children.
 * @param props - BTBreadcrumb props
 */
export const useBTBreadcrumb = (props: IBTBreadcrumbProps) => {
  const {
    children,
    testid,
    separator = "/",
    className,
    id,
    "aria-label": ariaLabel = "Breadcrumb",
  } = props;
  const useMdBreakpoint = useMediaQuery("(max-width: 768px)");

  const childArray = useMemo(() => flattenItems(children), [children]);
  const isCollapsible = childArray.length >= MIN_COLLAPSIBLE_ITEMS;

  // A string, so the layout effect's dep compares by VALUE. Depending on `childArray` instead
  // would force a synchronous reflow on every consumer render — children are built inline, so
  // its identity is always fresh — which is a real cost per keystroke in a withFormikBT title
  // field. Geometry changes arrive on the ResizeObserver, a separate path.
  const trailSignature = useMemo(
    () => getTrailSignature(childArray),
    [childArray],
  );

  // Collapse reacts to the space around the breadcrumb, not the window: a detail pane can be
  // narrow while the viewport is wide, and below md the split view goes single-pane so the
  // breakpoint never fires there at all. It stays on only as a floor.
  //
  // Both the observation and the measurement target the nav's PARENT, never the nav itself
  // (ado-285790). As a shrink-to-fit flex child the nav's width tracks its own content rather
  // than the space available, which made collapse one-way; and a nav-targeted observer is
  // silent for exactly the resizes that should expand it. The contract this places on the
  // parent is documented on `BTBreadcrumb`.
  const [isOverflowing, setIsOverflowing] = useState(false);
  const isOverflowingRef = useRef(false);
  const isCollapsibleRef = useRef(isCollapsible);
  const expandThresholdRef = useRef<number | null>(null);
  // The <nav> itself, for isWrapped(). Nulls on unmount (standard ref-callback contract), so a
  // racing parent-observer callback lands on the null guard in measure().
  const navRef = useRef<HTMLElement | null>(null);
  const trailSignatureRef = useRef<string | null>(null);

  const setOverflowing = useCallback((next: boolean) => {
    if (isOverflowingRef.current === next) {
      return;
    }
    isOverflowingRef.current = next;
    setIsOverflowing(next);
  }, []);

  const measure = useCallback(
    (parentEl: HTMLElement, navEl: HTMLElement | null) => {
      // navEl null-check: the parent observer can race a nav swap/unmount.
      if (!isCollapsibleRef.current || !navEl) {
        return;
      }

      // Always clientWidth, never the observer entry's contentRect.width: contentRect
      // excludes padding, so mixing the two would skew the hysteresis comparison by the
      // parent's padding.
      const width = parentEl.clientWidth;

      if (isOverflowingRef.current) {
        const threshold = expandThresholdRef.current;
        if (threshold === null || width > threshold + EXPAND_HYSTERESIS_PX) {
          expandThresholdRef.current = null;
          setOverflowing(false);
        }
        return;
      }

      if (isWrapped(navEl)) {
        expandThresholdRef.current = width;
        setOverflowing(true);
      }
    },
    [setOverflowing],
  );

  const [containerRef, onContainerRefChange] = useResizeObserver<HTMLElement>(
    useCallback(
      (entry) => {
        if (entry?.target) {
          measure(entry.target as HTMLElement, navRef.current);
        }
      },
      [measure],
    ),
  );

  // We need the nav for isWrapped() but its PARENT for measurement, and useResizeObserver has
  // no "observe X, track Y" mode — hence interposing here. Keep this identity-stable: a fresh
  // callback each render would tear down and rebuild the observer every time.
  const onNavRefChange = useCallback(
    (node: HTMLElement | null) => {
      navRef.current = node;
      // A detached node has no parent — forward null and skip measuring until reattach.
      onContainerRefChange(node ? node.parentElement : null);
    },
    [onContainerRefChange],
  );

  // Collapsing doesn't change the parent's width, so the ResizeObserver stays silent — without
  // this re-measure the row could expand and then sit wrapped. Pre-paint keeps the settling
  // invisible.
  //
  // This is the ratchet, and it's what makes expand/collapse converge rather than ping-pong: a
  // failed expand probe re-latches at the current, larger width, walking the threshold toward
  // the true fit width. EXPAND_HYSTERESIS_PX is not doing that work (see its doc).
  useLayoutEffect(() => {
    isCollapsibleRef.current = isCollapsible;

    if (!isCollapsible) {
      expandThresholdRef.current = null;
      setOverflowing(false);
      return;
    }

    // A latch belongs to the trail it was measured for, so a new trail re-probes from
    // scratch rather than inheriting a threshold that describes different content.
    if (trailSignatureRef.current !== trailSignature) {
      trailSignatureRef.current = trailSignature;
      expandThresholdRef.current = null;
    }

    if (containerRef.current) {
      measure(containerRef.current, navRef.current);
    }
  }, [
    isCollapsible,
    isOverflowing,
    trailSignature,
    containerRef,
    measure,
    setOverflowing,
  ]);

  const shouldCollapse = isCollapsible && (isOverflowing || useMdBreakpoint);

  const processedChildren = useMemo((): ReactNode => {
    if (shouldCollapse) {
      const first = childArray[0];
      const last = childArray[childArray.length - 1];
      const middle = childArray.slice(1, -1);

      const collapsedItem = (
        <CollapsedBreadcrumbItem
          key="collapsed"
          testid={`${testid}-collapsed`}
          separator={separator}
          collapsedItems={middle}
        />
      );

      return [first, collapsedItem, last];
    }

    return childArray;
  }, [childArray, shouldCollapse, testid, separator]);

  return {
    buiProps: {
      testid,
      separator,
      className: "bui-breadcrumb-container",
    },
    navProps: {
      id,
      ref: onNavRefChange,
      className: cn("bui-breadcrumb", className),
      "aria-label": ariaLabel,
    },
    processedChildren,
  };
};
